import { useCallback, useMemo, type CSSProperties } from 'react';
import type { Feed, FeedEntry } from '~/services/layout/index.ts';
import type { Action } from '~/hooks/actions/types.ts';
import defaultThemeV2Manifest from '~/services/plugins/default_theme_v2/config/manifest.ts';
import {
  getDefaultPlaylistActions,
  getPlaylistAliasAsset,
  getPlaylistAliasLabel,
  resolvePlaylistActionAssetFlavour,
} from './use-popup-menu-playlist.ts';
import type {
  PopupMenuAction,
  PopupMenuEntry,
  PopupMenuHeader,
  PopupMenuHeaderAction,
  PopupMenuSection,
} from '../types.ts';
import { isRecord } from '~/services/layout/layout.ts';

type ThemeManifestField = {
  key?: string;
  type?: string;
  initial_value?: string | number | boolean | null;
  fields?: readonly ThemeManifestField[];
};

const foundationEntry: PopupMenuEntry = {
  id: 'popup-menu-foundation-entry-1',
  title: 'Contextual actions',
  subtitle: 'Default contextual menu content',
  header: {
    titleAlignment: 'left',
  },
  triggerLabel: 'Open contextual actions',
  triggerIcon: 'more',
  closeButton: true,
  closeButtonLabel: 'Close',
  sections: [],
};

const popupMenuBaseIconMap: Record<string, string> = {
  play: '/popup-menu/add-to-queue.png',
  add_to_playlist: '/popup-menu/add-to-playlist.png',
  remove_item: '/menu.svg',
  delete_collection: '/menu.svg',
  delete_playlist: '/menu.svg',
  edit: '/menu.svg',
  edit_order: '/menu.svg',
  edit_name: '/menu.svg',
  edit_name_and_details: '/menu.svg',
  collection_list: '/menu.svg',
  sleep_timer: '/popup-menu/sleep-timer.png',
  playback_speed: '/popup-menu/playback-speed.png',
  go_to_artist: '/menu.svg',
  more: '/menu.svg',
  share: '/popup-menu/share.png',
  favorite: '/popup-menu/favorite.png',
  next: '/popup-menu/arrow-right.png',
  chevron: '/popup-menu/arrow-right.png',
  chevron_right: '/popup-menu/arrow-right.png',
  'chevron-right': '/popup-menu/arrow-right.png',
};

const popupMenuBaseAliasLabelMap: Record<string, string> = {
  add_to_playlist: 'Add To Playlist',
  remove_item: 'Remove Item',
  delete_collection: 'Delete Collection',
  edit: 'Edit Playlist',
  edit_name: 'Edit Name',
  collection_list: 'Go To Playlist',
  sleep_timer: 'Sleep Timer',
  playback_speed: 'Playback Speed',
  go_to_artist: 'Go To Artist',
};

function getQueueConfigLocalizations(
  queueConfig?: Record<string, unknown>
): Record<string, unknown> | undefined {
  const localizationsSource = queueConfig?.localizations;
  return isRecord(localizationsSource) ? localizationsSource : undefined;
}

function getQueueConfigCustomConfiguration(
  queueConfig?: Record<string, unknown>
): Record<string, unknown> | undefined {
  const customConfigurationSource = queueConfig?.customConfiguration;

  return isRecord(customConfigurationSource)
    ? customConfigurationSource
    : undefined;
}

function buildPopupMenuIconMap(
  queueConfig?: Record<string, unknown>
): Record<string, string> {
  const customConfiguration = getQueueConfigCustomConfiguration(queueConfig);

  const queueAddToQueueAsset = getStringValue(
    customConfiguration?.action_assets_flavor_1_add_to_queue_asset
  );
  const queueOpenQueueAsset = getStringValue(
    customConfiguration?.action_assets_flavor_1_open_queue_asset
  );

  const iconMap: Record<string, string> = {
    ...popupMenuBaseIconMap,
  };

  if (queueAddToQueueAsset) {
    iconMap.add_to_queue = queueAddToQueueAsset;
    iconMap.add_all_to_queue = queueAddToQueueAsset;
  }

  if (queueOpenQueueAsset) {
    iconMap.open_queue = queueOpenQueueAsset;
    iconMap.go_to_queue = queueOpenQueueAsset;
  }

  return iconMap;
}

function buildPopupMenuAliasLabelMap(
  queueConfig?: Record<string, unknown>
): Record<string, string> {
  const localizations = getQueueConfigLocalizations(queueConfig);

  const aliasLabelMap: Record<string, string> = {
    ...popupMenuBaseAliasLabelMap,
  };

  const addToQueueLabel = getStringValue(
    localizations?.add_to_queue_action_label
  );
  const addAllToQueueLabel = getStringValue(
    localizations?.add_all_to_queue_action_label
  );
  const openQueueLabel = getStringValue(localizations?.open_queue_action_label);
  const goToQueueLabel = getStringValue(
    localizations?.go_to_queue_action_label
  );
  const removeFromQueueLabel = getStringValue(
    localizations?.remove_from_queue_action_label
  );

  if (addToQueueLabel) aliasLabelMap.add_to_queue = addToQueueLabel;
  if (addAllToQueueLabel) aliasLabelMap.add_all_to_queue = addAllToQueueLabel;
  if (openQueueLabel) aliasLabelMap.open_queue = openQueueLabel;
  if (goToQueueLabel) aliasLabelMap.go_to_queue = goToQueueLabel;
  if (removeFromQueueLabel) {
    aliasLabelMap.remove_from_queue = removeFromQueueLabel;
  }

  return aliasLabelMap;
}

function getActionAlias(source: Record<string, unknown>): string | undefined {
  const actionButton = isRecord(source.button) ? source.button : undefined;

  return getStringValue(
    actionButton?.alias,
    actionButton?.id,
    source.alias,
    source.action_alias
  );
}

function getCollectionQueueItems(entry?: Record<string, unknown>): FeedEntry[] {
  if (!entry) {
    return [];
  }

  const entryExtensions = isRecord(entry.extensions) ? entry.extensions : {};
  const entryContent = isRecord(entry.content) ? entry.content : {};

  const candidateLists: unknown[] = [
    entry.entry,
    entry.entries,
    entry.items,
    entryExtensions.items,
    entryContent.items,
  ];

  for (const candidateList of candidateLists) {
    if (!Array.isArray(candidateList)) {
      continue;
    }

    const queueItems = candidateList
      .filter((value): value is Record<string, unknown> => isRecord(value))
      .filter((item) => {
        const id = getStringValue(item.id, item.identifier);
        return typeof id === 'string' && id.length > 0;
      }) as FeedEntry[];

    if (queueItems.length > 0) {
      return queueItems;
    }
  }

  return [];
}

function getDefaultQueueActions({
  alias,
  entry,
}: {
  alias?: string;
  entry?: Record<string, unknown>;
}): Action[] {
  if (alias === 'open_queue' || alias === 'go_to_queue') {
    return [
      {
        type: 'openQueue',
        options: {},
      },
    ];
  }

  if (alias === 'add_to_queue') {
    return [
      {
        type: 'addToQueue',
        options: entry ? { entry: entry as FeedEntry } : {},
      },
    ];
  }

  if (alias === 'add_all_to_queue') {
    const collectionQueueItems = getCollectionQueueItems(entry);

    if (collectionQueueItems.length > 0) {
      return [
        {
          type: 'addAllToQueue',
          options: {
            entries: collectionQueueItems,
          },
        },
      ];
    }

    const entryId = getStringValue(entry?.id, entry?.identifier);

    if (!entryId) {
      return [];
    }

    return [
      {
        type: 'addAllToQueue',
        options: {
          entries: [entry as FeedEntry],
        },
      },
    ];
  }

  return [];
}

function normalizeActionActions({
  source,
  normalizedAlias,
  hasNavigationLink,
  sourceEntry,
  playlistConfig,
}: {
  source: Record<string, unknown>;
  normalizedAlias?: string;
  hasNavigationLink: boolean;
  sourceEntry?: Record<string, unknown>;
  playlistConfig?: Record<string, unknown>;
}): Action[] {
  const rawActions = Array.isArray(source.actions) ? source.actions : [];

  const normalizedActions = rawActions.filter((value): value is Action =>
    isRecord(value)
  ) as Action[];

  if (normalizedActions.length > 0) return normalizedActions;

  if (hasNavigationLink) return [];

  const defaultPlaylistActions = getDefaultPlaylistActions({
    alias: normalizedAlias,
    sourceEntry,
    playlistConfig,
  });

  if (defaultPlaylistActions.length > 0) return defaultPlaylistActions;

  return getDefaultQueueActions({
    alias: normalizedAlias,
    entry: sourceEntry,
  });
}

function getHeaderAction({
  source,
  fallbackId,
}: {
  source: unknown;
  fallbackId: string;
}): PopupMenuHeaderAction | undefined {
  if (!isRecord(source)) return undefined;

  const label = getStringValue(source.label, source.title, source.text);

  if (!label) return undefined;

  const id = getStringValue(source.id, source.identifier) || fallbackId;

  return {
    id,
    label,
  };
}

function getHeaderConfig(entry: Record<string, unknown>): PopupMenuHeader {
  const entryHeader = isRecord(entry.header) ? entry.header : undefined;

  const titleAlignmentSource = getStringValue(
    entryHeader?.titleAlignment,
    entryHeader?.alignment,
    entry.header_title_alignment,
    entry.titleAlignment
  );

  const titleAlignment =
    titleAlignmentSource?.toLowerCase() === 'center' ? 'center' : 'left';

  const leadingAction = getHeaderAction({
    source:
      entryHeader?.leadingAction ||
      entryHeader?.leftAction ||
      entry.leadingAction ||
      entry.leftAction,
    fallbackId: 'header-leading-action',
  });

  const trailingAction = getHeaderAction({
    source:
      entryHeader?.trailingAction ||
      entryHeader?.rightAction ||
      entry.trailingAction ||
      entry.rightAction,
    fallbackId: 'header-trailing-action',
  });

  return {
    titleAlignment,
    leadingAction,
    trailingAction,
  };
}

function getPopupMenuDefaultCssVars(): Record<string, string> {
  const vars: Record<string, string> = {};

  const collectField = (field: ThemeManifestField) => {
    if (Array.isArray(field.fields)) {
      field.fields.forEach(collectField);
      return;
    }

    if (!field.key) return;
    if (!field.key.startsWith('popup_menu_')) return;
    if (
      field.initial_value === null ||
      typeof field.initial_value === 'undefined'
    ) {
      return;
    }

    if (
      field.type === 'number_input' &&
      typeof field.initial_value === 'number'
    ) {
      vars[`--${field.key}`] = `${field.initial_value}px`;
      return;
    }

    vars[`--${field.key}`] = String(field.initial_value);
  };

  const themeFields =
    defaultThemeV2Manifest.custom_configuration_fields as readonly ThemeManifestField[];
  themeFields.forEach(collectField);

  const desktopFigmaFallbackVars: Record<string, string> = {
    '--popup_menu_desktop_background_color': '#111111',
    '--popup_menu_desktop_border_color': 'rgba(239, 239, 239, 0.1)',
    '--popup_menu_desktop_shadow_color': 'rgba(0, 0, 0, 0.35)',
    '--popup_menu_text_label_desktop_default_color':
      'rgba(239, 239, 239, 0.65)',
    '--popup_menu_text_label_desktop_hover_color': '#efefef',
    '--popup_menu_text_label_desktop_pressed_color': '#efefef',
    '--popup_menu_menu_item_desktop_default_background_color': 'transparent',
    '--popup_menu_menu_item_desktop_hover_background_color': '#2e2e2e',
    '--popup_menu_menu_item_desktop_active_background_color': '#2e2e2e',
    '--popup_menu_divider_desktop_color': 'rgba(239, 239, 239, 0.1)',
    '--popup_menu_icons_mobile_unselected_color': 'rgba(239, 239, 239, 0.65)',
    '--popup_menu_icons_mobile_selected_color': '#efefef',
  };

  Object.assign(vars, desktopFigmaFallbackVars);

  return vars;
}

const popupMenuDefaultCssVars = getPopupMenuDefaultCssVars();

function getStringValue(...values: unknown[]): string | undefined {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value;
  }

  return undefined;
}

function normalizeAction({
  action,
  index,
  parentId,
  sourceEntry,
  uiComponentStyles,
  playlistConfig,
  popupMenuAliasLabelMap,
}: {
  action: unknown;
  index: number;
  parentId: string;
  sourceEntry?: Record<string, unknown>;
  uiComponentStyles?: Record<string, unknown>;
  playlistConfig?: Record<string, unknown>;
  popupMenuAliasLabelMap: Record<string, string>;
}): PopupMenuAction | null {
  if (!isRecord(action)) return null;

  const actionAlias = getActionAlias(action);
  const normalizedAlias = actionAlias?.toLowerCase();

  // Web V1 does not support reordering playlists (action alias: 'edit')
  if (normalizedAlias === 'edit') return null;

  const isQueueOpenAlias =
    normalizedAlias === 'open_queue' || normalizedAlias === 'go_to_queue';
  const actionAssetFlavour = resolvePlaylistActionAssetFlavour({
    source: action,
    uiComponentStyles,
  });
  const actionLink = isRecord(action.link) ? action.link : undefined;
  const url = getStringValue(action.url, action.href, actionLink?.href);
  const hasNavigationLink = isQueueOpenAlias ? false : Boolean(url);
  const normalizedActions = normalizeActionActions({
    source: action,
    normalizedAlias,
    hasNavigationLink,
    sourceEntry,
    playlistConfig,
  });

  const label =
    getStringValue(action.label, action.title, action.name) ||
    (normalizedAlias ? popupMenuAliasLabelMap[normalizedAlias] : undefined);

  if (!label) return null;

  const id =
    getStringValue(action.id, action.identifier, actionAlias) ||
    `${parentId}-action-${index}`;

  const childrenSource =
    (Array.isArray(action.children) && action.children) ||
    (Array.isArray(action.actions) && action.actions) ||
    [];

  const children = childrenSource
    .map((childAction, childIndex) => {
      return normalizeAction({
        action: childAction,
        index: childIndex,
        parentId: id,
        sourceEntry,
        uiComponentStyles,
        playlistConfig,
        popupMenuAliasLabelMap,
      });
    })
    .filter(Boolean) as PopupMenuAction[];

  return {
    id,
    label,
    alias: actionAlias,
    actionAssetFlavour,
    icon:
      getStringValue(action.icon, action.leadingIcon) ||
      (normalizedAlias ? normalizedAlias : undefined),
    url: isQueueOpenAlias ? undefined : url,
    href: isQueueOpenAlias
      ? undefined
      : getStringValue(action.href, action.url),
    dismissOnAction:
      typeof action.dismiss_on_action === 'boolean'
        ? action.dismiss_on_action
        : true,
    actions: normalizedActions,
    children,
  };
}

/**
 * Returns the first id not yet used: the base id itself, otherwise the base
 * id with the lowest free numeric suffix (`favorite-1`, `favorite-2`, ...).
 */
function getUniqueActionId({
  baseId,
  usedIds,
}: {
  baseId: string;
  usedIds: Set<string>;
}): string {
  if (!usedIds.has(baseId)) {
    return baseId;
  }

  let suffix: number = 1;

  while (usedIds.has(`${baseId}-${suffix}`)) {
    suffix += 1;
  }

  return `${baseId}-${suffix}`;
}

/**
 * Suffixes repeated action ids within a list (and recursively within each
 * action's children) so React keys and action paths stay unique when a feed
 * repeats an action, e.g. two favourites items.
 */
export function withUniqueActionIds(
  actions: PopupMenuAction[]
): PopupMenuAction[] {
  const usedIds = new Set<string>();

  return actions.map((action: PopupMenuAction): PopupMenuAction => {
    const id: string = getUniqueActionId({ baseId: action.id, usedIds });

    usedIds.add(id);

    return {
      ...action,
      id,
      children: action.children && withUniqueActionIds(action.children),
    };
  });
}

function normalizeSections({
  entry,
  uiComponentStyles,
  playlistConfig,
  popupMenuAliasLabelMap,
}: {
  entry: Record<string, unknown>;
  uiComponentStyles?: Record<string, unknown>;
  playlistConfig?: Record<string, unknown>;
  popupMenuAliasLabelMap: Record<string, string>;
}): PopupMenuSection[] {
  const entryExtensions = isRecord(entry.extensions)
    ? entry.extensions
    : undefined;
  const contextualEntryActions = entryExtensions?.entry_action;

  if (Array.isArray(contextualEntryActions)) {
    const contextualActions = contextualEntryActions
      .map((entryAction, actionIndex) => {
        return normalizeAction({
          action: entryAction,
          index: actionIndex,
          parentId: 'section-contextual-actions',
          sourceEntry: entry,
          uiComponentStyles,
          playlistConfig,
          popupMenuAliasLabelMap,
        });
      })
      .filter(Boolean) as PopupMenuAction[];

    if (contextualActions.length) {
      return [
        {
          id: 'section-contextual-actions',
          label: getStringValue(entry.sectionLabel, 'Actions'),
          actions: withUniqueActionIds(contextualActions),
        },
      ];
    }
  }

  if (Array.isArray(entry.sections)) {
    const mappedSections = entry.sections
      .map((section, sectionIndex) => {
        if (!isRecord(section)) return null;

        const sectionActions = (
          Array.isArray(section.actions) ? section.actions : []
        )
          .map((action, actionIndex) => {
            return normalizeAction({
              action,
              index: actionIndex,
              parentId: `section-${sectionIndex}`,
              sourceEntry: entry,
              uiComponentStyles,
              playlistConfig,
              popupMenuAliasLabelMap,
            });
          })
          .filter(Boolean) as PopupMenuAction[];

        if (!sectionActions.length) return null;

        return {
          id:
            getStringValue(section.id, section.identifier) ||
            `section-${sectionIndex}`,
          label: getStringValue(section.label, section.title),
          actions: withUniqueActionIds(sectionActions),
        };
      })
      .filter(Boolean) as PopupMenuSection[];

    if (mappedSections.length) return mappedSections;
  }

  const singleSectionActions = (
    Array.isArray(entry.actions) ? entry.actions : []
  )
    .map((action, actionIndex) => {
      return normalizeAction({
        action,
        index: actionIndex,
        parentId: 'section-actions',
        sourceEntry: entry,
        uiComponentStyles,
        playlistConfig,
        popupMenuAliasLabelMap,
      });
    })
    .filter(Boolean) as PopupMenuAction[];

  if (!singleSectionActions.length) return [];

  return [
    {
      id: 'section-actions',
      label: getStringValue(entry.sectionLabel) || 'Actions',
      actions: withUniqueActionIds(singleSectionActions),
    },
  ];
}

function getFoundationEntry({
  feed,
  uiComponentStyles,
  playlistConfig,
  popupMenuAliasLabelMap,
}: {
  feed?: Feed;
  uiComponentStyles?: Record<string, unknown>;
  playlistConfig?: Record<string, unknown>;
  popupMenuAliasLabelMap: Record<string, string>;
}): PopupMenuEntry {
  const maybeEntry = feed?.entry?.[0] as unknown;

  if (!isRecord(maybeEntry)) return foundationEntry;

  const mergedTitle =
    typeof maybeEntry.title === 'string'
      ? maybeEntry.title
      : foundationEntry.title;

  const mergedSubtitle = getStringValue(
    maybeEntry.subtitle,
    maybeEntry.summary
  );

  const feedTrigger = isRecord(maybeEntry.trigger)
    ? maybeEntry.trigger
    : undefined;

  const triggerLabel =
    getStringValue(
      maybeEntry.triggerLabel,
      maybeEntry.trigger_label,
      feedTrigger?.label,
      feedTrigger?.title
    ) || foundationEntry.triggerLabel;

  const triggerIcon = getStringValue(
    maybeEntry.triggerIcon,
    maybeEntry.trigger_icon,
    feedTrigger?.icon,
    feedTrigger?.leadingIcon
  );

  const closeButton =
    typeof maybeEntry.closeButton === 'boolean'
      ? maybeEntry.closeButton
      : foundationEntry.closeButton;

  const closeButtonLabel =
    getStringValue(maybeEntry.closeButtonLabel, maybeEntry.close_label) ||
    foundationEntry.closeButtonLabel;

  const sections = normalizeSections({
    entry: maybeEntry,
    uiComponentStyles,
    playlistConfig,
    popupMenuAliasLabelMap,
  });
  const header = getHeaderConfig(maybeEntry);

  return {
    ...foundationEntry,
    // Preserve source entry metadata for downstream action context fallbacks.
    ...maybeEntry,
    title: mergedTitle,
    subtitle: mergedSubtitle || foundationEntry.subtitle,
    header,
    triggerLabel,
    triggerIcon: triggerIcon || foundationEntry.triggerIcon,
    closeButton,
    closeButtonLabel,
    sections,
  };
}

export function usePopupMenuData({
  feed,
  uiComponentStyles,
  playlistConfig,
  queueConfig,
  isThemeV2 = false,
}: {
  feed?: Feed;
  uiComponentStyles?: Record<string, unknown>;
  playlistConfig?: Record<string, unknown>;
  queueConfig?: Record<string, unknown>;
  isThemeV2?: boolean;
}) {
  const popupMenuAliasLabelMap = useMemo(() => {
    return buildPopupMenuAliasLabelMap(queueConfig);
  }, [queueConfig]);

  const popupMenuIconMap = useMemo(() => {
    return buildPopupMenuIconMap(queueConfig);
  }, [queueConfig]);

  const entry = useMemo(() => {
    return getFoundationEntry({
      feed,
      uiComponentStyles,
      playlistConfig,
      popupMenuAliasLabelMap,
    });
  }, [feed, popupMenuAliasLabelMap, uiComponentStyles, playlistConfig]);

  const getIconSrc = useCallback(
    (icon?: string, action?: PopupMenuAction): string | undefined => {
      if (!icon) return undefined;

      const normalizedIcon = icon.trim().toLowerCase();

      if (!normalizedIcon) return undefined;

      const playlistAsset = getPlaylistAliasAsset({
        alias: action?.alias,
        icon: normalizedIcon,
        actionAssetFlavour: resolvePlaylistActionAssetFlavour({
          source: { action_asset_flavour: action?.actionAssetFlavour },
        }),
        playlistConfig,
      });

      if (playlistAsset) return playlistAsset;

      return popupMenuIconMap[normalizedIcon];
    },
    [playlistConfig, popupMenuIconMap]
  );

  const withPlaylistLabelsEntry = useMemo(() => {
    const mapAction = (action: PopupMenuAction): PopupMenuAction => {
      const playlistLabel = getPlaylistAliasLabel({
        alias: action.alias,
        playlistConfig,
      });

      return {
        ...action,
        label: playlistLabel || action.label,
        children: (action.children || []).map(mapAction),
      };
    };

    return {
      ...entry,
      sections: entry.sections.map((section) => {
        return {
          ...section,
          actions: section.actions.map(mapAction),
        };
      }),
    };
  }, [entry, playlistConfig]);

  const componentStyle = useMemo(() => {
    if (isThemeV2) return undefined;

    const hasConfiguredStyles = Boolean(
      uiComponentStyles && Object.keys(uiComponentStyles).length
    );

    if (hasConfiguredStyles) return undefined;

    return popupMenuDefaultCssVars as CSSProperties;
  }, [isThemeV2, uiComponentStyles]);

  return {
    entry: withPlaylistLabelsEntry,
    getIconSrc,
    componentStyle,
  };
}
